import clsx from 'clsx';
import { useMemo } from 'react';
import { useLocalStorageState } from 'ahooks';

import { useSearch } from '@/hooks/use-search';
import { useItemActions } from '@/hooks/use-item-actions';
import { useBlockLazy, useNote } from '@/hooks/rxdb-models';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Menu } from '@flintmint/web-shared/components/menu';
import { Switch } from '@flintmint/web-shared/components/switch';
import { Popover } from '@flintmint/web-shared/components/popover';

import { itemTypes } from '@/constants/item-types';
import { getBlockStorage } from '@/utils/editor-utils';
import { conditionalSpread } from '@/utils/helper-utils';

import type { SlashMenuCommonProps } from '@/components/slash-menu';
import type { SearchableDocument } from '@/services/search-service/orama-schema';
import type { Block } from '@flintmint/core-shared/rxdb';

import styles from './note-slash-menu.module.scss';

export const NoteSlashMenu = (props: SlashMenuCommonProps) => {
  const { query, editor, command, className, makeGenerateTextMenuItem, generateTextActive, forwardedRef, ...rest } = props;

  const [includeContext = true, setIncludeContext] = useLocalStorageState<boolean>('note-include-context', {
    defaultValue: true,
  });

  const { query: blockQuery } = useBlockLazy();
  const { data } = useSearch({ term: query, where: { types: ['blocks'] } });

  const { data: note } = useNote(editor.storage.documentInfo.documentId!);

  const { createNew } = useItemActions();

  const renderIncludeContextMenu = (
    <Popover
      arrow
      maxWidth={240}
      placement="right"
      offset={[0, 20]}
      className={clsx(styles.noteContextSwitch, 'context-switch')}
      onContentClick={(e) => e.stopPropagation()}
      content={
        <>
          <Switch
            label="Include Context"
            checked={includeContext}
            data-testid="include-context-toggle"
            onChange={() => {
              setIncludeContext(!includeContext);
            }}
          />
          <p>
            When Include Context is enabled AI will include Instructions and Cortex Items as well as Note’s contents for the
            generated content.
          </p>
        </>
      }
    >
      <Icon
        type="more-vertical"
        className={styles.contextIcon}
        data-testid="context-popover-trigger"
        onClick={(e) => e.stopPropagation()}
      />
    </Popover>
  );

  const renderBlockItem = (block: SearchableDocument) => (
    <>
      <div className={styles.nameHolder}>
        <Icon type={itemTypes['blocks']?.icon} size={16} />
        <span>{block.name}</span>
      </div>
      <div>
        <span className="ellipsis-text">{block.content.slice(0, 80)}</span>
      </div>
    </>
  );

  const generateTextOption = makeGenerateTextMenuItem({
    includeContext,
    headerAction: renderIncludeContextMenu,
    llmContext: { note },
  });

  const menu = useMemo(
    () => [
      generateTextOption,
      ...conditionalSpread(
        [
          { type: 'divider' },
          {
            type: 'item',
            name: 'Create Block',
            icon: 'plus-sign-square',
            onClick: () => {
              createNew({
                type: 'blocks',
                prompt: '',
                context: {
                  editor,
                  autoApplyChanges: true,
                  noteId: editor.storage.documentInfo.documentId!,
                },
              });
              command();
            },
          },
          ...conditionalSpread(
            [
              { type: 'divider' },
              {
                name: 'Insert Block',
                type: 'group',
              },
            ],
            !!data.length
          ),
          ...data.flatMap(({ document: block }, index) => [
            {
              type: 'item',
              className: styles.blockItem,
              renderer: () => renderBlockItem(block),
              onClick: () => {
                blockQuery(block.id).then((doc) => {
                  if (doc) {
                    editor
                      .chain()
                      .focus()
                      .deleteRange(rest.range)
                      .insertBlock(doc.toJSON() as Block)
                      .run();
                  }
                });
              },
            },
            ...conditionalSpread(
              [
                {
                  type: 'divider',
                  className: styles.blockDivider,
                },
              ],
              index !== data.length - 1
            ),
          ]),
        ],
        !getBlockStorage(editor).focusedBlockInstanceId
      ),
    ],
    [includeContext, data]
  );

  return (
    <Menu
      data={menu}
      className={className}
      forwardedRef={forwardedRef}
      data-testid="note-slash-menu"
      onExitSubMenu={() => editor.view.focus()}
      initialActiveCustomView={generateTextActive ? generateTextOption : undefined}
    />
  );
};
